import { Star } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useSandboxImagesByKind } from '@/hooks'
import type { SandboxImageKind } from '@/types/sandboxImage'

interface SandboxImageSelectorProps {
  kind: SandboxImageKind
  value: string | null
  onChange: (value: string | null) => void
  defaultLabel?: string
  testId?: string
}

const NULL_VALUE = '__none__'

export function SandboxImageSelector({
  kind,
  value,
  onChange,
  defaultLabel = '(use default)',
  testId,
}: SandboxImageSelectorProps) {
  const { data: images, isLoading } = useSandboxImagesByKind(kind)

  if (isLoading) {
    return (
      <Select disabled>
        <SelectTrigger className="w-full" data-testid={testId}>
          <SelectValue placeholder="Loading..." />
        </SelectTrigger>
      </Select>
    )
  }

  if (!images || images.length === 0) {
    return (
      <Select disabled>
        <SelectTrigger className="w-full" data-testid={testId}>
          <SelectValue placeholder="No images available — contact your platform admin" />
        </SelectTrigger>
      </Select>
    )
  }

  return (
    <Select
      value={value ?? NULL_VALUE}
      onValueChange={(val) => onChange(!val || val === NULL_VALUE ? null : val)}
    >
      <SelectTrigger className="w-full" data-testid={testId}>
        <SelectValue placeholder={defaultLabel} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NULL_VALUE}>{defaultLabel}</SelectItem>
        {images.map((image) => (
          <SelectItem
            key={image.id}
            value={image.id}
            disabled={image.isDeprecated}
            className={image.isDeprecated ? 'opacity-50' : ''}
          >
            <span className="flex items-center gap-2">
              {image.isBaseline && <Star className="h-3.5 w-3.5 text-warning-foreground" aria-label="baseline" />}
              <span>{image.name}</span>
              {image.runtimes && (
                <span className="text-xs text-muted-foreground">— {image.runtimes}</span>
              )}
              {image.isDeprecated && (
                <span className="text-xs text-muted-foreground">(deprecated)</span>
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
